import * as React from "react";
import Link from "next/link";
import { CellMeasurer, CellMeasurerCache, createMasonryCellPositioner, Masonry, AutoSizer, WindowScroller } from "react-virtualized";
import { H3 } from "../theme/text";
import styled from "react-emotion";
import { greyColors, primaryColors } from "../theme/colors";
import { css } from "emotion";
import { spacing } from "../theme/layout";
import { h4 } from "../theme/typefaces";
import { BREAKPOINTS } from "../theme/layout";
import { spaces } from "../theme/layout/spacing";
import { createFetcher } from "./Fetcher";
import { fetchDataSet } from "../api/dataset";
import { fetchSlice } from "../api/slice";
import { Loader } from "./Loader";
import { ErrorAlert } from "./Alerts";
import { getFacetLabel, getTimeValue, getLocation, getLocationsFromEntity, getValue, filterByGeoEntity, getLocationLabel, getAvailableEntities, getMappableEntities } from "../detail/getters";
import { MiniChartPanel } from "../detail/MiniChartPanel";
const DataSetFetcher = createFetcher({ fetch: fetchDataSet });
const SliceFetcher = createFetcher({ fetch: fetchSlice });
export const GridContainer = styled("div") `
  margin: 0 ${-spaces[1]}px;
`;
const CellContainer = styled("a") `
  display: block;
  width: 100%;
  text-decoration: none;
  ${spacing.pa1};
`;
export const cellStyle = css `
  label: cell;
  min-height: 300px;
  height: 300px;
  max-height: 300px;
  overflow: hidden;

  ${spacing.pa2};
  color: ${primaryColors.darkGrey};
  cursor: pointer;

  transition: box-shadow 0.3s;
  &:hover {
    box-shadow: 0 1px 10px 0 rgba(0, 0, 0, 0.25);
  }
  & h4 {
    ${h4};
    ${spacing.mt0};
    ${spacing.mh0};
    ${spacing.mb2};
    ${spacing.pa0};
  }
`;
export const cellContent = css `
  display: flex;
  flex-flow: row wrap;
  justify-content: space-around;
`;
export const formatLabel = css `
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;
export class SearchResultsGrid extends React.Component {
    constructor(props) {
        super(props);
        this.cellRenderer = ({ index, key, parent, style }) => {
            const result = this.props.searchResults[index];
            const backgroundColor = this.props.page === "search" ? greyColors["50"] : primaryColors.white;
            // Safety check – shouldn't happen
            if (!result) {
                if (process.env.NODE_ENV === "development") {
                    console.warn(`No cell at index ${index} – skipping cellRender`);
                }
                return;
            }
            return (<div key={key}>
        <CellMeasurer cache={this._cache} index={index} key={key} parent={parent} resultsCount={this.props.resultsCount}>
          <Link href={{ pathname: "/dataset", query: { id: result.id } }} as={`/dataset/${result.id}`} passHref>
            <CellContainer style={{
                ...style,
                width: this._columnWidth
            }}>
              <div className={cellStyle} style={{
                backgroundColor
            }}>                   
                <H3 className={formatLabel} title={result.label}>{result.label}</H3>
                <DataSetFetcher fetchArg={result.id}>
                  {({ loading, error, data: metadata }) => {
                if (error) {
                    return <ErrorAlert text={"Ups!"}/>;
                }
                if (loading) {
                    return <Loader />;
                }
                if (!metadata) {
                    return null;
                }
                const mappableEntities = getMappableEntities(metadata)[0];
                const availableEntities = getAvailableEntities(metadata)[0];
                const selectedGeoEntity = mappableEntities
                    ? mappableEntities
                    : availableEntities;
                return (<SliceFetcher fetchArg={{ uri: metadata.dataURI, raum: "R30000" }}>
                        {({ loading, error, data }) => {
                    if (!data) {
                        return null;
                    }
                    const filteredByGeoEntityData = filterByGeoEntity(data.observations, getLocationsFromEntity(selectedGeoEntity));
                    return (<div className={cellContent}>
                              <MiniChartPanel unit={metadata.unit} getTimeValue={getTimeValue} getLocation={getLocation} getLocationLabel={getLocationLabel(metadata)} getFacet={getFacetLabel(metadata)} getMeasureValue={getValue} locations={getLocationsFromEntity(selectedGeoEntity)} selectedGeoEntity={selectedGeoEntity} width={this._columnWidth - 2 * this.CARD_CONTENT_PADDING} metadata={metadata} data={filteredByGeoEntityData}/>
                            </div>);
                }}
                      </SliceFetcher>);
            }}
                </DataSetFetcher>
              </div>
            </CellContainer>
          </Link>
        </CellMeasurer>
      </div>);
        };
        this.initCellPositioner = () => {
            if (typeof this._cellPositioner === "undefined") {
                this._cellPositioner = createMasonryCellPositioner({
                    cellMeasurerCache: this._cache,
                    columnCount: this._columnCount,
                    columnWidth: this._columnWidth,
                    spacer: 0
                });
            }
        };
        this.resetCellPositioner = () => {
            this._cellPositioner.reset({
                columnCount: this._columnCount,
                columnWidth: this._columnWidth,
                spacer: 0
            });
        };
        this.onResize = ({ width }) => {
            this._width = width;
            this.calculateColumnCount();
            this.resetCellPositioner();
            this._masonry.recomputeCellPositions();
        };
        this.renderAutoSizer = ({ height, scrollTop }) => {
            this._height = height;
            this._scrollTop = scrollTop;
            return (<div>
        <AutoSizer resultsCount={this.props.resultsCount} // Force update!
             disableHeight height={height} onResize={this.onResize} scrollTop={this._scrollTop}>
          {this.renderMasonry}
        </AutoSizer>
      </div>);
        };
        this.renderMasonry = ({ width }) => {
            this._width = width;
            this.calculateColumnCount();
            this.initCellPositioner();
            return (<Masonry className={css `
          :focus {
            outline: 0;
          }
        `} autoHeight={true} cellCount={this.props.resultsCount} cellMeasurerCache={this._cache} cellPositioner={this._cellPositioner} cellRenderer={this.cellRenderer} height={this._height} ref={this.setMasonryRef} scrollTop={this._scrollTop} width={width}/>);
        };
        this.setMasonryRef = (ref) => {
            this._masonry = ref;
        };
        this.setWindowScrollerRef = (ref) => {
            this._windowScroller = ref;
        };
        this._columnCount = 3;
        this._columnWidth = 450;
        this._width = 0;
        this._height = 0;
        this._scrollTop = 0;
        this._cellPositioner = undefined;
        this._cache = new CellMeasurerCache({
            defaultHeight: 250,
            defaultWidth: 400,
            fixedWidth: true
        });
        this.CARD_CONTENT_PADDING = 16;
    }
    calculateColumnCount() {
        if (this._width > BREAKPOINTS[5][0]) {
            // 1920+
            this._columnCount = 4;
            // columnWidth = total width / number of columns
            this._columnWidth = this._width / 4;
        }
        else if (this._width > BREAKPOINTS[4][0]) {
            // 1439+
            this._columnCount = 3;
            this._columnWidth = this._width / 3;
        }
        else if (this._width > BREAKPOINTS[2][0]) {
            // 639+
            this._columnCount = 2;
            this._columnWidth = this._width / 2;
        }
        else {
            this._columnCount = 1;
            this._columnWidth = this._width;
        }
    }
    componentDidMount() {
        this._windowScroller.updatePosition();
    }
    componentWillReceiveProps(nextProps) {
        if (nextProps.resultsCount !== this.props.resultsCount) {
            this._cache.clearAll();
            this.calculateColumnCount();
            this.resetCellPositioner();
            this._masonry.clearCellPositions();
        }
    }
    render() {
        let masonry;
        return (<GridContainer>
        <WindowScroller resultsCount={this.props.resultsCount} // Force update!
         ref={this.setWindowScrollerRef}>
          {this.renderAutoSizer}
        </WindowScroller>
      </GridContainer>);
    }
}



// WEBPACK FOOTER //
// ./src/components/SearchResultsGrid.tsx